import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "lucide-react";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { useDispatch, useSelector } from "react-redux";
import { addTab, setActiveTab, setListViewState } from "../../../redux/slices/locationSlice";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import { fetchLocationsByGrouped } from "../Manage-Team/api";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../TableSavedViews";

const SORTABLE_COLUMNS = {
  "Location Name": "locationName",
  "Parent Name":   "parentName",
  "Site":          "site",
  "Address":       "address",
  "Location Type": "locationType",
  "Office Code":   "officeCode",
  "Notes":         "notes",
};

const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Location Name",
  "Parent Name",
  "Site",
  "Address",
  "Location Type",
  "Contact Info",
  "Office Code",
  "Notes",
];

const locationTypeBadgeClass = {
  PICKUP:    "bg-blue-100   dark:bg-blue-900/40   text-black dark:text-blue-200",
  DELIVERY:  "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  BOTH:      "bg-purple-100  dark:bg-purple-900/40  text-black dark:text-purple-200",
  POINT:     "bg-sky-100    dark:bg-sky-900/40     text-black dark:text-sky-200",
  GEOGRAPHY: "bg-amber-100  dark:bg-amber-900/40   text-black dark:text-amber-200",
  WAREHOUSE: "bg-gray-100   dark:bg-gray-700/40    text-black dark:text-gray-200",
  DEFAULT:   "bg-gray-100   dark:bg-gray-700/40    text-black dark:text-gray-200",
};

// Non-sticky data cell — NO background so <tr> hover shows through
const tdBase =
  "px-2 sm:px-3 py-1 sm:py-2 " +
  "text-xs sm:text-sm md:text-base " +
  "text-gray-900 dark:text-acs-dark-text " +
  "border-b border-gray-200 dark:border-acs-dark-border " +
  "transition-none font-roboto cursor-pointer";

const TypeBadge = ({ typeStr }) => {
  const key = (typeStr || "").toUpperCase();
  return (
    <span className={`text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${locationTypeBadgeClass[key] || locationTypeBadgeClass.DEFAULT}`}>
      {typeStr || "-"}
    </span>
  );
};

const formatContact = (loc) =>
  [loc.contactName || loc.contactPerson, loc.phoneNumber, loc.email]
    .filter(Boolean).join(" | ") || "-";

const LocationsTable = ({ searchQuery = "", setTotalLocations }) => {
  const dispatch = useDispatch();
  const { tabs } = useSelector(selectModuleTabs("locations"));
  const { listViewState = {} } = useSelector((s) => s.locations);
  const savedViewState = listViewState["locations-table"] || {};

  const [currentPage,      setCurrentPage]      = useState(savedViewState.currentPage      || 1);
  const [locationsPerPage, setLocationsPerPage] = useState(savedViewState.locationsPerPage  || 50);
  const [sortBy,           setSortBy]           = useState(savedViewState.sortBy            || null);
  const [ascending,        setAscending]        = useState(savedViewState.ascending         ?? true);
  const [allLocations,     setAllLocations]     = useState([]);
  const [isLoading,        setIsLoading]        = useState(true);
  const [openMenu,         setOpenMenu]         = useState(null);
  const [dropdownPos,      setDropdownPos]      = useState({ top: 0, left: 0 });

  const dropdownRef        = useRef(null);
  const checkboxHeaderRef  = useRef(null);
  const prevSearchQueryRef = useRef(searchQuery);
  const lastOpenedRef      = useRef({ id: null, time: 0 });
  const sortedCacheRef = useRef({ key: null, value: [] });

  const getExtraConfig = useCallback(
    () => ({
      sortBy,
      ascending,
      currentPage,
      pageSize: locationsPerPage,
    }),
    [sortBy, ascending, currentPage, locationsPerPage]
  );

  const handleApplySavedViewConfig = useCallback((config) => {
    if (config.sortBy !== undefined) setSortBy(config.sortBy);
    if (config.ascending !== undefined) setAscending(config.ascending);
    if (config.currentPage) setCurrentPage(config.currentPage);
    if (config.pageSize) setLocationsPerPage(config.pageSize);
  }, []);

  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.LOCATIONS,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Location Name"],
    getExtraConfig,
    onApplyConfig: handleApplySavedViewConfig,
  });

  const {
    columnWidths: savedColumnWidths,
    setColumnWidths: onColumnResize,
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    markDirty,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    dispatch(setListViewState({ stateKey: "locations-table", currentPage, locationsPerPage, sortBy, ascending }));
  }, [dispatch, currentPage, locationsPerPage, sortBy, ascending]);

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const loadLocations = async () => {
    setIsLoading(true);
    try {
      const response  = await fetchLocationsByGrouped();
      const locations = Array.isArray(response.data) ? response.data : [];
      setAllLocations(locations);
      if (setTotalLocations) setTotalLocations(locations.length);
    } catch (error) {
      console.error("Error fetching locations:", error);
      setAllLocations([]);
      if (setTotalLocations) setTotalLocations(0);
    } finally {
      setIsLoading(false);
    }
  };

  const matchesFields = (item) => {
    const q = searchQuery.toLowerCase();
    return (
      (item.locationName || "").toLowerCase().includes(q) ||
      (item.parentName   || "").toLowerCase().includes(q) ||
      (item.site         || "").toLowerCase().includes(q) ||
      (item.address      || "").toLowerCase().includes(q) ||
      (item.locationType || "").toLowerCase().includes(q) ||
      (item.officeCode   || "").toLowerCase().includes(q) ||
      (item.notes        || "").toLowerCase().includes(q)
    );
  };

  const filteredLocations = React.useMemo(
    () =>
      allLocations.filter((loc) =>
        !searchQuery.trim() || matchesFields(loc)
      ),
    [allLocations, searchQuery]
  );

  useEffect(() => {
    const searchChanged = prevSearchQueryRef.current !== searchQuery;
    prevSearchQueryRef.current = searchQuery;
    if (setTotalLocations) setTotalLocations(filteredLocations.length);
    if (searchChanged) setCurrentPage(1);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (headerLabel) => {
    markDirty?.();
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    if (sortBy === key) setAscending((prev) => !prev);
    else { setSortBy(key); setAscending(true); }
    setCurrentPage(1);
  };

  const sortedLocations = React.useMemo(() => {
    const cacheKey = [
      sortBy || "none",
      ascending ? "asc" : "desc",
      searchQuery,
      allLocations.length,
      filteredLocations.length,
    ].join("|");
    if (sortedCacheRef.current.key === cacheKey) {
      return sortedCacheRef.current.value;
    }
    if (!sortBy) {
      sortedCacheRef.current = { key: cacheKey, value: filteredLocations };
      return filteredLocations;
    }
    const sorted = [...filteredLocations].sort((a, b) => {
      const aVal = (a[sortBy] ?? "").toString().toLowerCase();
      const bVal = (b[sortBy] ?? "").toString().toLowerCase();
      if (aVal < bVal) return ascending ? -1 : 1;
      if (aVal > bVal) return ascending ? 1 : -1;
      return 0;
    });
    sortedCacheRef.current = { key: cacheKey, value: sorted };
    return sorted;
  }, [filteredLocations, sortBy, ascending, searchQuery, allLocations.length]);

  const totalPages = Math.max(1, Math.ceil(sortedLocations.length / locationsPerPage));
  const paginatedLocations = sortedLocations.slice(
    (currentPage - 1) * locationsPerPage,
    currentPage * locationsPerPage
  );

  const handleViewLocation = (locationId, locationName, locationData) => {
    const now = Date.now();
    if (lastOpenedRef.current.id === locationId && now - lastOpenedRef.current.time < 500) return;
    lastOpenedRef.current = { id: locationId, time: now };

    const existingIndex = tabs.findIndex(
      (t) => t.contentType === "locationView" && t.locationId === locationId
    );
    if (existingIndex !== -1) { dispatch(setActiveTab({ index: existingIndex })); return; }

    dispatch(addTab({
      id:          `location-${locationId}-${Date.now()}`,
      name:        locationName || `Location ${locationId}`,
      contentType: "locationView",
      locationId,
      data:        locationData,
      isDynamic:   true,
      createdAt:   Date.now(),
    }));
  };

  const formattedLocations = React.useMemo(
    () =>
      paginatedLocations.map((loc) => ({
        _id:   loc.id,
        _name: loc.locationName,
        _raw:  loc,
        Selection: null,
        Action:    null,
        "Location Name": loc.locationName || "-",
        "Parent Name":   loc.parentName   || "-",
        Site:            loc.site         || "-",
        Address:         loc.address      || "-",
        "Location Type": <TypeBadge typeStr={loc.locationType} />,
        "Contact Info":  formatContact(loc),
        "Office Code":   loc.officeCode   || "-",
        Notes: loc.notes
          ? loc.notes.length > 40 ? loc.notes.substring(0, 40) + "…" : loc.notes
          : "-",
      })),
    [paginatedLocations]
  );

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Locations…" />
      ) : (
        <>
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={viewsError}
              isDirty={isDirty}
              allDataHeaders={TABLE_HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          </div>
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={formattedLocations}
              checkboxHeaderRef={checkboxHeaderRef}
              // ── Pinning ──────────────────────────────────────────────────
              enableColumnPin
              // ── Sizing ───────────────────────────────────────────────────
              enableColumnResize
              dataColumnMinWidth={200}
              dataColumnMaxWidth={420}
              initialColumnWidths={savedColumnWidths}
              onColumnResize={onColumnResize}
              // ── Reordering / column manager ──────────────────────────────
              enableColumnReorder
              nonHideableColumnIds={["Location Name"]}
              // ── Sorting ──────────────────────────────────────────────────
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              onSort={handleSort}
              sortBy={sortBy}
              ascending={ascending}
              sortKeyByHeader={(label) => SORTABLE_COLUMNS[label]}
            >
              {(row, _rowIdx, colWidths, getStickyCellProps, visibleColumns) => {
                const id     = row._id;
                const isOpen = openMenu === id;

                const { className: checkboxClass, style: checkboxStyle } =
                  getStickyCellProps("Selection");
                const { className: actionClass, style: actionStyle } =
                  getStickyCellProps("Action");

                // Visible data columns in current pin + drag order.
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((cid) => cid !== "Selection" && cid !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length
                  ? orderedDataHeaders
                  : dataHeaders;

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`border-b border-gray-200 dark:border-acs-dark-border z-[10] text-center transition-none ${checkboxClass}`}
                      style={{ ...checkboxStyle, width: "80px", minWidth: "80px", maxWidth: "80px" }}
                      onDoubleClick={() => handleViewLocation(id, row._name, row._raw)}
                    >
                      <input
                        type="checkbox"
                        className="cursor-pointer mx-auto block"
                        onClick={(e) => e.stopPropagation()}
                        readOnly
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${actionClass}`}
                      style={{ ...actionStyle, width: "50px", minWidth: "50px", maxWidth: "50px" }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen
                          moduleName="Location"
                          onClose={() => setOpenMenu(null)}
                          actions={[
                            { icon: "Eye", label: "View", color: "blue", action: () => handleViewLocation(id, row._name, row._raw) },
                          ]}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Columns 2+ — Data in current pin + drag order */}
                    {dataHeadersToRender.map((header) => {
                      const { className: colClass, style: colStyle } = getStickyCellProps(header);
                      const colWidth = colWidths?.[header] ?? 200;
                      return (
                        <td
                          key={header}
                          className={`${tdBase} ${colClass}`}
                          style={{
                            ...colStyle,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        `${colWidth}px`,
                            minWidth:     `${colWidth}px`,
                            maxWidth:     `${colWidth}px`,
                          }}
                          onDoubleClick={() => handleViewLocation(id, row._name, row._raw)}
                        >
                          {row[header]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>
          </div>

          <GlobalPagination
            label="Locations per page"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={locationsPerPage}
            onItemsPerPageChange={(n) => { setLocationsPerPage(n); setCurrentPage(1); }}
          />
        </>
      )}
    </div>
  );
};

export default LocationsTable;
